import { Box, Button, Text } from '@chakra-ui/react';
import { Dialog } from '@halo/ui-react';
import type { User } from '@halo/chat-sdk-react';
import { Empty, SearchInput, UserItem } from '@halo/ui-chat-react';
import { useContactsStore } from '@/store/contacts';
import { useStaticAppStore } from '@/store/client';

export interface ContactsAddDialogProps {
  reset: boolean;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onClose: () => void;
  onAddContact: (user: User) => void;
}

export function ContactsAddDialog({
  reset,
  isOpen,
  setIsOpen,
  onAddContact,
}: ContactsAddDialogProps) {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const deferredSearchValue = useDeferredValue(searchValue);
  // const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);

  const { client } = useStaticAppStore();
  const { contacts } = useContactsStore();

  const { run: onSearch } = useRequest(
    async (keywords: string) => {
      let _ = keywords;
      if (_.startsWith('@')) _ = _.slice(1);
      if (!_) return [];
      const res = await client?.Api.contacts.contactsSearchByBeyInGlobal(_);
      return res ?? [];
    },
    {
      debounceWait: 300,
      manual: true,
      onSuccess: (data) => setUsers(data),
      onError: () => setUsers([]),
    }
  );

  const handleAddContact = useCallback(
    async (user: User) => {
      onAddContact(user);
    },
    [onAddContact]
  );

  useEffect(() => {
    onSearch(deferredSearchValue);
  }, [deferredSearchValue, onSearch]);

  useEffect(() => {
    if (!(isOpen && reset)) return;
    setSearchValue('');
    setUsers([]);
  }, [isOpen, reset, setSearchValue]);

  return (
    <Dialog.Root open={isOpen} onOpenChange={(details) => setIsOpen(details.open)} unmountOnExit>
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content pt="2.5" px="5" pb="5" w="560px" maxW="100%">
          <Dialog.Header
            px="0"
            py="2.5"
            fontSize="md"
            fontWeight="semibold"
            justifyContent="space-between"
          >
            <Text>{t('action.addContact')}</Text>
            {/* <Center cursor="pointer" onClick={onClose}>
                <CloseIcon />
              </Center> */}
          </Dialog.Header>
          <Dialog.Body p="0" py="2">
            <SearchInput value={searchValue} onChange={(value) => setSearchValue(value)} />
            <Box pt="2">
              <Box
                className="scrollHover"
                borderRadius="lg"
                shadow="md"
                p="1"
                maxH="300px"
                overflowY="auto"
              >
                {!users?.length ? (
                  <Box p="5">
                    <Empty gap="0" py="0" />
                  </Box>
                ) : (
                  users.map((user) => {
                    const isKeyErr = !(user.have_group_key && user.have_user_key);
                    const added = contacts.some((contact) => contact.user_id === user.user_id);
                    return (
                      <UserItem
                        key={user.user_id}
                        {...user}
                        error={isKeyErr}
                        userExtraElement={
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={added || isKeyErr}
                            _disabled={{
                              color: 'fg.subtle',
                              cursor: 'not-allowed',
                            }}
                            onClick={() => handleAddContact(user)}
                          >
                            {t('buttons.add')}
                          </Button>
                        }
                      />
                    );
                  })
                )}
              </Box>
            </Box>
          </Dialog.Body>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
